import { useState, useEffect } from 'react';
import { FileSearch, MessageSquare, CheckCircle, Plus, Loader2, ArrowRight, List } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

interface OfficeActionTaskGuideProps {
  onCloseWizard: () => void;
  onAddApplication: (appNumber: string) => Promise<void>;
  onNavigateToExamination: (applicationNumber: string) => void;
  onApplicationSelected?: (applicationNumber: string) => void;
  sessionApplications?: any[];
}

export function OfficeActionTaskGuide({ onCloseWizard, onAddApplication, onNavigateToExamination, onApplicationSelected, sessionApplications = [] }: OfficeActionTaskGuideProps) {
  const [mode, setMode] = useState<'select' | 'new'>('select');
  const [selectedAppId, setSelectedAppId] = useState<string | null>(null);
  const [appNumber, setAppNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [appComplete, setAppComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingAppId, setLoadingAppId] = useState<string | null>(null);

  // If there are no existing applications, default to "new" mode
  useEffect(() => {
    if (sessionApplications.length === 0) {
      setMode('new');
    }
  }, [sessionApplications]);

  // Listen for job completion events
  useEffect(() => {
    const handleJobComplete = (event: CustomEvent) => {
      const { docType, docId, success, error: jobError } = event.detail;

      // Only handle if it's the application we're loading
      if (docType === 'application' && docId === loadingAppId) {
        setIsLoading(false);
        if (success) {
          console.log('[OfficeActionTaskGuide] New application loaded successfully:', docId);
          setAppComplete(true);
          setError(null);
          setAppNumber(docId); // Make sure appNumber is set to the loaded ID

          // Add to session context (same pattern as ExaminationPage)
          const storedContext = localStorage.getItem('session_context');
          const context = storedContext && storedContext !== 'undefined' ? JSON.parse(storedContext) : {};
          const currentApps = context.current_applications || [];
          if (!currentApps.includes(docId)) {
            context.current_applications = [...currentApps, docId];
            localStorage.setItem('session_context', JSON.stringify(context));
            window.dispatchEvent(new Event('storage-update'));
          }

          onApplicationSelected?.(docId);
        } else {
          setError(jobError || 'Failed to load application');
          setLoadingAppId(null);
        }
      }
    };

    window.addEventListener('job-complete', handleJobComplete as EventListener);

    return () => {
      window.removeEventListener('job-complete', handleJobComplete as EventListener);
    };
  }, [loadingAppId, onApplicationSelected]);

  const handleAddApplication = async () => {
    if (!appNumber.trim()) {
      setError('Please enter an application number');
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      // Clean the app number to get the ID that will be used
      let cleanedId = appNumber.trim()
        .replace(/,/g, '')
        .replace(/\//g, '')
        .replace(/^US/i, '')
        .replace(/[A-Z]\d*$/i, '');

      setLoadingAppId(cleanedId);
      await onAddApplication(appNumber.trim());
      // Don't set appComplete here - wait for job-complete event
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load application');
      setIsLoading(false);
      setLoadingAppId(null);
    }
  };

  const handleSelectExisting = () => {
    if (selectedAppId) {
      console.log('[OfficeActionTaskGuide] Selected existing app:', selectedAppId);
      setAppComplete(true);
      setAppNumber(selectedAppId);

      // Add to session context (same pattern as ExaminationPage)
      const storedContext = localStorage.getItem('session_context');
      const context = storedContext && storedContext !== 'undefined' ? JSON.parse(storedContext) : {};
      const currentApps = context.current_applications || [];
      if (!currentApps.includes(selectedAppId)) {
        context.current_applications = [...currentApps, selectedAppId];
        localStorage.setItem('session_context', JSON.stringify(context));
        window.dispatchEvent(new Event('storage-update'));
      }

      onApplicationSelected?.(selectedAppId);
    }
  };

  const handleGoToExamination = () => {
    const appId = appNumber || selectedAppId;
    console.log('[OfficeActionTaskGuide] Navigating to examination with app:', appId);
    if (appId) {
      onNavigateToExamination(appId);
    }
    onCloseWizard();
  };

  const canProceed = mode === 'select' ? selectedAppId : appComplete;

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-gray-900">Examine an Office Action</h2>
        <p className="text-gray-600">
          Analyze rejections and plan your response strategy
        </p>
      </div>

      <div className="space-y-4">
        {/* Step 1: Select or Add Application */}
        <div className={`border-2 rounded-lg p-5 transition-colors ${
          canProceed
            ? 'border-green-200 bg-green-50'
            : 'border-purple-200 bg-purple-50'
        }`}>
          <div className="flex items-start gap-4">
            <div className={`w-8 h-8 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold ${
              canProceed ? 'bg-green-600' : 'bg-purple-600'
            }`}>
              {canProceed ? <CheckCircle className="w-5 h-5" /> : '1'}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-2">
                {canProceed ? 'Application Ready!' : 'Select or Add Application'}
              </h3>

              {!canProceed ? (
                <>
                  {/* Mode Toggle */}
                  {sessionApplications.length > 0 && (
                    <div className="flex gap-2 mb-3">
                      <Button
                        variant={mode === 'select' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setMode('select')}
                        className="gap-2"
                      >
                        <List className="w-4 h-4" />
                        Select Existing
                      </Button>
                      <Button
                        variant={mode === 'new' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setMode('new')}
                        className="gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Add New
                      </Button>
                    </div>
                  )}

                  {mode === 'select' ? (
                    <>
                      <p className="text-sm text-gray-600 mb-3">
                        Select an existing application from your session
                      </p>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {sessionApplications.map((app) => {
                          const appId = app.application_id || app.application?.applicationNumberText || 'Unknown';
                          const isSelected = selectedAppId === appId;
                          return (
                            <button
                              key={appId}
                              onClick={() => setSelectedAppId(appId)}
                              className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                                isSelected
                                  ? 'border-purple-500 bg-purple-50'
                                  : 'border-gray-200 bg-white hover:border-purple-300'
                              }`}
                            >
                              <div className="font-medium text-gray-900">{appId}</div>
                              {app.application?.inventionTitle && (
                                <div className="text-xs text-gray-600 mt-1 truncate">
                                  {app.application.inventionTitle}
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      <Button
                        onClick={handleSelectExisting}
                        disabled={!selectedAppId}
                        className="gap-2 bg-purple-600 hover:bg-purple-700 mt-3"
                        size="sm"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Use This Application
                      </Button>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-gray-600 mb-3">
                        Enter a US application number to load its data
                      </p>
                      <div className="space-y-3">
                        <Input
                          type="text"
                          placeholder="16/123,456 or 16123456"
                          value={appNumber}
                          onChange={(e) => setAppNumber(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !isLoading) {
                              handleAddApplication();
                            }
                          }}
                          disabled={isLoading}
                          className="bg-white"
                        />
                        {error && (
                          <p className="text-sm text-red-600">{error}</p>
                        )}
                        <Button
                          onClick={handleAddApplication}
                          disabled={isLoading || !appNumber.trim()}
                          className="gap-2 bg-purple-600 hover:bg-purple-700"
                          size="sm"
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Loading Application...
                            </>
                          ) : (
                            <>
                              <Plus className="w-4 h-4" />
                              Load Application
                            </>
                          )}
                        </Button>
                        <div className="text-xs text-gray-600 bg-white/50 rounded px-3 py-2">
                          <strong>Example:</strong> Enter "16/123,456" or "16123456"
                        </div>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-600">
                  Application <strong>{appNumber || selectedAppId}</strong> is ready.
                  Now upload the office action for this application.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Step 2: Go to Examination Page */}
        {canProceed && (
          <div className="border-2 border-blue-200 rounded-lg p-5 bg-blue-50">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                2
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">View Prosecution Documents</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Navigate to the Examination page to view all prosecution documents, office actions, and perform analysis for <strong>{appNumber || selectedAppId}</strong>
                </p>
                <Button
                  onClick={handleGoToExamination}
                  className="gap-2 bg-blue-600 hover:bg-blue-700"
                  size="sm"
                >
                  <FileSearch className="w-4 h-4" />
                  Go to Examination Page
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* What You Can Do */}
        {canProceed && (
          <div className="border-2 border-purple-200 rounded-lg p-5 bg-purple-50">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center flex-shrink-0">
                <FileSearch className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">Analyze the Office Action</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Once uploaded, you can ask Patent Loop to:
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-2 text-gray-700">
                    <MessageSquare className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                    <span>Summarize the rejections and key issues</span>
                  </div>
                  <div className="flex items-start gap-2 text-gray-700">
                    <MessageSquare className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                    <span>Explain the examiner's reasoning</span>
                  </div>
                  <div className="flex items-start gap-2 text-gray-700">
                    <MessageSquare className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                    <span>Suggest response strategies</span>
                  </div>
                  <div className="flex items-start gap-2 text-gray-700">
                    <MessageSquare className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                    <span>Compare to prior art citations</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pro Tip */}
        {canProceed && (
          <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-lg p-4 border border-amber-200">
            <p className="text-sm text-gray-700">
              <strong className="text-amber-900">💡 Pro Tip:</strong> Click on any cited USC section, MPEP section,
              or CFR rule in the office action to view the full text instantly in a side-by-side viewer.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
